/**
 * VoxelMeshComponent - renders voxel meshes with palette-based coloring.
 * Supports loading MagicaVoxel (.vox) files via the `voxPath` property,
 * making it fully usable in the editor without any runtime setup code.
 */

import * as THREE from 'three';

import { projectInfo } from '../../storage/projectContext.js';
import { AssetPath } from '../../storage/storageProvider.js';
import { EngineClass } from '../../systems/ClassRegistry.js';
import { AsyncTaskWorker, IAsyncLoadableTag } from '../../utils/IAsyncLoadable.js';
import { property, VoxelPath } from '../../utils/serialization/decorator.js';
import { PrimitiveComponent } from '../PrimitiveComponent.js';

import type { EditorPropertyChangedResult } from '../../utils/EditorClassMeta.js';
import type { IAsyncLoadable } from '../../utils/IAsyncLoadable.js';
import type { PrimitiveComponentOptions } from '../PrimitiveComponent.js';

export type Palette = Record<number, THREE.ColorRepresentation>;

export type VoxelState = {
  sizeX: number;
  sizeY: number;
  sizeZ: number;
  voxelSize: number;
  voxels: Uint16Array;
  palette: Palette;
}

/**
 * Palette override entry - maps a 1-based MagicaVoxel palette index to a CSS/hex color string.
 * Stored as an array so the editor can render it as a list of per-index overrides.
 */
export type PaletteEntry = {
  index: number;
  color: string;
};

export interface VoxelMeshComponentOptions extends PrimitiveComponentOptions {
  voxPath?: string;
  voxelSize?: number;
  paletteOverrides?: PaletteEntry[];
}

/**
 * VoxelMeshComponent - renders voxel-based meshes.
 * Set `voxPath` to a `.vox` file to load it automatically in both editor and runtime.
 * Use `paletteOverrides` to tint individual palette indices without editing the source file.
 */
@EngineClass('VoxelMeshComponent')
export class VoxelMeshComponent extends PrimitiveComponent implements IAsyncLoadable {
  readonly [IAsyncLoadableTag] = true;

  private state?: VoxelState;
  private mesh!: THREE.Mesh;

  private _asyncTaskWorker = new AsyncTaskWorker();

  @property({ type: 'voxelPath', required: true, description: 'MagicaVoxel .vox file path' })
  public voxPath: VoxelPath | null = null;

  @property({ type: 'number', min: 0.01, step: 0.1, decimals: 3, description: 'World units per voxel' })
  public voxelSize: number = 1;

  @property({
    type: 'array',
    description: 'Per-index color overrides (index = 1-based MagicaVoxel palette index)',
    child: {
      type: 'object',
      fields: {
        index: { type: 'number', min: 1, max: 255, step: 1, decimals: 0 },
        color: { type: 'color' },
      },
    },
  })
  public paletteOverrides: PaletteEntry[] = [];

  constructor() {
    super();
    this.createMesh();
  }

  public override initialize(options?: VoxelMeshComponentOptions): void {
    super.initialize(options);
    this.voxPath = options?.voxPath ?? this.voxPath;
    this.voxelSize = options?.voxelSize ?? this.voxelSize;
    this.paletteOverrides = options?.paletteOverrides ?? this.paletteOverrides;
  }

  public override postLoad(): void {
    super.postLoad();
    if (this.voxPath) {
      this._loadVox(this.voxPath);
    }
  }

  public override beginPlay(): void {
    super.beginPlay();
    // Avoid double-loading if postLoad already triggered a load.
    if (this.voxPath && !this.state && !this._asyncTaskWorker.isRunning()) {
      this._loadVox(this.voxPath);
    }
  }

  public async waitForLoad(): Promise<void> {
    await this._asyncTaskWorker.waitUntilComplete();
  }

  public isLoading(): boolean {
    return this._asyncTaskWorker.isRunning();
  }

  // ------------------------------------------------------------------
  // Private loading
  // ------------------------------------------------------------------

  private _loadVox(url: string): void {
    if (!url || !url.trim()) return;
    this._asyncTaskWorker.runTask(async () => {
      try {
        const path = AssetPath.fromString(url);
        const buf = await projectInfo.storageProvider.downloadFileAsBuffer(path);
        if (!buf) {
          console.error(`VoxelMeshComponent: failed to download ${url}`);
          return;
        }

        const { size, voxels, palette } = parseVOX(buf);

        // MagicaVoxel uses Y-up; swizzle to Three.js Y-up: keep X, swap Y<->Z
        const [sizeX, sizeY, sizeZ] = defaultOrientation(size.x, size.y, size.z);

        const paletteMap: Palette = {};
        palette.forEach((c, i) => (paletteMap[i + 1] = c));

        // Apply any editor overrides on top
        for (const entry of this.paletteOverrides) {
          if (entry.index >= 1 && entry.index <= 255) {
            paletteMap[entry.index] = entry.color;
          }
        }

        this.state = {
          sizeX,
          sizeY,
          sizeZ,
          voxelSize: this.voxelSize,
          voxels: new Uint16Array(sizeX * sizeY * sizeZ),
          palette: paletteMap,
        };

        for (const v of voxels) {
          const [vx, vy, vz] = defaultOrientation(v.x, v.y, v.z);
          const index = vx + sizeX * (vy + sizeY * vz);
          if (index >= 0 && index < this.state.voxels.length) {
            this.state.voxels[index] = v.color;
          }
        }

        this._rebuildMesh();
      } catch (err) {
        console.error(`VoxelMeshComponent: error loading ${url}:`, err);
      }
    }).catch(err => {
      console.error(`VoxelMeshComponent: unhandled error loading ${url}:`, err);
    });
  }

  private _rebuildMesh(materialOverride?: THREE.Material): void {
    if (!this.state) return;
    if (materialOverride) {
      this.mesh.material = materialOverride;
    }
    const geometry = greedyMesh(
      this.state.voxels,
      this.state.sizeX,
      this.state.sizeY,
      this.state.sizeZ,
      this.state.voxelSize,
      this.state.palette,
    );
    this.mesh.geometry.dispose();
    this.mesh.geometry = geometry;
    this.bindObject3DProperties(this.mesh, ['castShadow', 'receiveShadow']);
    this.refreshPhysics();
  }

  private createMesh(): void {
    this.mesh = new THREE.Mesh(
      new THREE.BufferGeometry(),
      new THREE.MeshBasicMaterial({ vertexColors: true })
    );
    this.add(this.mesh);
  }

  // ------------------------------------------------------------------
  // Manual / programmatic API (kept for backwards-compat / runtime use)
  // ------------------------------------------------------------------

  public initializeVoxel(sizeX: number, sizeY: number, sizeZ: number, voxelSize: number, palette: Palette): void {
    this.state = {
      sizeX,
      sizeY,
      sizeZ,
      voxelSize,
      voxels: new Uint16Array(sizeX * sizeY * sizeZ),
      palette,
    };
    this.mesh.geometry.dispose();
  }

  public getVoxelState(): VoxelState | undefined {
    return this.state;
  }

  public getVoxel(x: number, y: number, z: number): number {
    if (!isValidLocation(x, y, z, this.state)) {
      return 0;
    }
    const index = getIndex(x, y, z, this.state, false);
    return this.state!.voxels[index];
  }

  public setVoxel(x: number, y: number, z: number, id: number): boolean {
    if (!isValidLocation(x, y, z, this.state)) {
      return false;
    }
    const index = getIndex(x, y, z, this.state, false);
    this.state!.voxels[index] = id;
    return true;
  }

  /**
   * Build the mesh using a greedy meshing algorithm to optimize geometry.
   * @param materialOverride - Optional material to use instead of the default
   */
  public buildMesh(materialOverride?: THREE.Material): void {
    this._rebuildMesh(materialOverride);
  }

  public getMesh(): THREE.Mesh {
    return this.mesh;
  }

  //====================================================================================================
  // Editor Functions
  //====================================================================================================

  public override getEditorClassIcon(): string | null {
    return 'Icon_VoxelMesh';
  }

  public override onEditorPropertyChanged(path: string, value: any, result: EditorPropertyChangedResult): void {
    super.onEditorPropertyChanged(path, value, result);
    if (path === 'voxPath') {
      if (value) {
        this._loadVox(value);
      } else {
        // Clear the mesh when path is cleared
        this.state = undefined;
        this.mesh.geometry.dispose();
        this.mesh.geometry = new THREE.BufferGeometry();
        this.refreshPhysics();
      }
    } else if (path === 'voxelSize' || path.startsWith('paletteOverrides')) {
      // Reload to apply new voxel size or palette overrides
      if (this.voxPath) {
        this._loadVox(this.voxPath);
      }
    }
  }

  public override getConfigurationWarnings(): string[] {
    const warnings = super.getConfigurationWarnings();
    if (!this.voxPath) {
      warnings.push('No .vox file assigned. Set the Vox Path property to display a voxel mesh.');
    }
    return warnings;
  }
}

//====================================================================================================

function isValidLocation(x: number, y: number, z: number, state?: VoxelState): boolean {
  if (!state) {
    return false;
  }
  return x >= 0 && y >= 0 && z >= 0 && x < state.sizeX && y < state.sizeY && z < state.sizeZ;
}

function getIndex(x: number, y: number, z: number, state?: VoxelState, validateLocation: boolean = true): number {
  if (validateLocation && !isValidLocation(x, y, z, state)) {
    return 0;
  }
  return x + state!.sizeX * (y + state!.sizeY * z);
}

/** Swizzle MagicaVoxel (X right, Y forward, Z up) to Three.js (X right, Y up, Z forward) */
function defaultOrientation(x: number, y: number, z: number): [number, number, number] {
  return [x, z, y];
}

// ------------------------------------------------------------------
// VOX format parser (spec v150)
// ------------------------------------------------------------------

interface VOXModel {
  size: { x: number; y: number; z: number };
  voxels: { x: number; y: number; z: number; color: number }[];
  palette: THREE.Color[];
}

function parseVOX(buffer: ArrayBuffer): VOXModel {
  const dv = new DataView(buffer);
  const text = (o: number) => String.fromCharCode(dv.getUint8(o), dv.getUint8(o + 1), dv.getUint8(o + 2), dv.getUint8(o + 3));

  if (text(0) !== 'VOX ') throw new Error('Not a VOX file');
  const version = dv.getUint32(4, true);
  if (version !== 150) console.warn(`VOX version ${version} (expected 150)`);

  let ptr = 8;
  const readChunk = () => {
    const id = text(ptr); ptr += 4;
    const size = dv.getUint32(ptr, true); ptr += 4;
    const childrenSize = dv.getUint32(ptr, true); ptr += 4;
    const chunkStart = ptr;
    ptr += size;
    return { id, size, chunkStart, childrenEnd: chunkStart + size + childrenSize };
  };

  const state: VOXModel = {
    size: { x: 0, y: 0, z: 0 },
    voxels: [],
    palette: defaultVOXPalette.map(c => new THREE.Color(c)),
  };

  const main = readChunk();
  if (main.id !== 'MAIN') throw new Error('Invalid VOX: no MAIN chunk');
  const stop = main.childrenEnd;

  while (ptr < stop) {
    const { id, size, chunkStart } = readChunk();
    switch (id) {
      case 'SIZE': {
        state.size.x = dv.getInt32(chunkStart, true);
        state.size.y = dv.getInt32(chunkStart + 4, true);
        state.size.z = dv.getInt32(chunkStart + 8, true);
        break;
      }
      case 'XYZI': {
        const n = dv.getUint32(chunkStart, true);
        let off = chunkStart + 4;
        for (let i = 0; i < n; i++) {
          const x = dv.getUint8(off++);
          const y = dv.getUint8(off++);
          const z = dv.getUint8(off++);
          const color = dv.getUint8(off++);
          state.voxels.push({ x, y, z, color });
        }
        break;
      }
      case 'RGBA': {
        let off = chunkStart;
        for (let i = 0; i < 256; i++) {
          const r = dv.getUint8(off++);
          const g = dv.getUint8(off++);
          const b = dv.getUint8(off++);
          off++; // alpha is unused
          state.palette[i] = new THREE.Color(r / 255, g / 255, b / 255);
        }
        break;
      }
      // Ignore MATL, nTRN, etc.
    }
  }
  return state;
}

// prettier-ignore
const defaultVOXPalette: number[] = [
  0x00000000,0xffffffff,0xffccffff,0xff99ffff,0xff66ffff,0xff33ffff,0xff00ffff,0xffffccff,
  0xffccccff,0xff99ccff,0xff66ccff,0xff33ccff,0xff00ccff,0xffff99ff,0xffcc99ff,0xff9999ff,
  0xff6699ff,0xff3399ff,0xff0099ff,0xffff66ff,0xffcc66ff,0xff9966ff,0xff6666ff,0xff3366ff,
  0xff0066ff,0xffff33ff,0xffcc33ff,0xff9933ff,0xff6633ff,0xff3333ff,0xff0033ff,0xffff00ff,
  0xffcc00ff,0xff9900ff,0xff6600ff,0xff3300ff,0xff0000ff,0xffffffcc,0xffccffcc,0xff99ffcc,
  0xff66ffcc,0xff33ffcc,0xff00ffcc,0xffffcccc,0xffcccccc,0xff99cccc,0xff66cccc,0xff33cccc,
  0xff00cccc,0xffff99cc,0xffcc99cc,0xff9999cc,0xff6699cc,0xff3399cc,0xff0099cc,0xffff66cc,
  0xffcc66cc,0xff9966cc,0xff6666cc,0xff3366cc,0xff0066cc,0xffff33cc,0xffcc33cc,0xff9933cc,
  0xff6633cc,0xff3333cc,0xff0033cc,0xffff00cc,0xffcc00cc,0xff9900cc,0xff6600cc,0xff3300cc,
  0xff0000cc,0xffffff99,0xffccff99,0xff99ff99,0xff66ff99,0xff33ff99,0xff00ff99,0xffffcc99,
  0xffcccc99,0xff99cc99,0xff66cc99,0xff33cc99,0xff00cc99,0xffff9999,0xffcc9999,0xff999999,
  0xff669999,0xff339999,0xff009999,0xffff6699,0xffcc6699,0xff996699,0xff666699,0xff336699,
  0xff006699,0xffff3399,0xffcc3399,0xff993399,0xff663399,0xff333399,0xff003399,0xffff0099,
  0xffcc0099,0xff990099,0xff660099,0xff330099,0xff000099,0xffffff66,0xffccff66,0xff99ff66,
  0xff66ff66,0xff33ff66,0xff00ff66,0xffffcc66,0xffcccc66,0xff99cc66,0xff66cc66,0xff33cc66,
  0xff00cc66,0xffff9966,0xffcc9966,0xff999966,0xff669966,0xff339966,0xff009966,0xffff6666,
  0xffcc6666,0xff996666,0xff666666,0xff336666,0xff006666,0xffff3366,0xffcc3366,0xff993366,
  0xff663366,0xff333366,0xff003366,0xffff0066,0xffcc0066,0xff990066,0xff660066,0xff330066,
  0xff000066,0xffffff33,0xffccff33,0xff99ff33,0xff66ff33,0xff33ff33,0xff00ff33,0xffffcc33,
  0xffcccc33,0xff99cc33,0xff66cc33,0xff33cc33,0xff00cc33,0xffff9933,0xffcc9933,0xff999933,
  0xff669933,0xff339933,0xff009933,0xffff6633,0xffcc6633,0xff996633,0xff666633,0xff336633,
  0xff006633,0xffff3333,0xffcc3333,0xff993333,0xff663333,0xff333333,0xff003333,0xffff0033,
  0xffcc0033,0xff990033,0xff660033,0xff330033,0xff000033,0xffffff00,0xffccff00,0xff99ff00,
  0xff66ff00,0xff33ff00,0xff00ff00,0xffffcc00,0xffcccc00,0xff99cc00,0xff66cc00,0xff33cc00,
  0xff00cc00,0xffff9900,0xffcc9900,0xff999900,0xff669900,0xff339900,0xff009900,0xffff6600,
  0xffcc6600,0xff996600,0xff666600,0xff336600,0xff006600,0xffff3300,0xffcc3300,0xff993300,
  0xff663300,0xff333300,0xff003300,0xffff0000,0xffcc0000,0xff990000,0xff660000,0xff330000,
  0xff0000ee,0xff0000dd,0xff0000bb,0xff0000aa,0xff000088,0xff000077,0xff000055,0xff000044,
  0xff000022,0xff000011,0xff110000,0xff220000,0xff440000,0xff550000,0xff770000,0xff880000,
  0xffaa0000,0xffbb0000,0xffdd0000,0xffee0000,0xffff0000,0xffee1100,0xffdd2200,0xffbb4400,
  0xffaa5500,0xff885500,0xff774400,0xff553300,0xff442200,0xff221100,0xff113300,0xff225500,
  0xff447700,0xff559900,0xff66aa00,0xff77bb00,0xff88cc00,0xff99dd00,0xffbbee00,0xffccff00,
  0xffddff00,0xffeeff00,0xffffff00,0xff0000,
];

function greedyMesh(
  voxels: Uint16Array,
  sx: number,
  sy: number,
  sz: number,
  scale: number,
  palette: Palette,
): THREE.BufferGeometry {
  const pos: number[] = [];
  const nrm: number[] = [];
  const col: number[] = [];
  const uv: number[] = [];
  const idx: number[] = [];
  let vert = 0;

  const dims = [sx, sy, sz] as const;
  const coord = [0, 0, 0] as number[];
  const q = [0, 0, 0] as number[];

  for (let d = 0; d < 3; d++) {
    const u = (d + 1) % 3;
    const v = (d + 2) % 3;
    q[0] = q[1] = q[2] = 0; q[d] = 1;

    const mask = new Int32Array(dims[u] * dims[v]);

    for (coord[d] = -1; coord[d] < dims[d]; coord[d]++) {
      let m = 0;
      for (coord[v] = 0; coord[v] < dims[v]; coord[v]++) {
        for (coord[u] = 0; coord[u] < dims[u]; coord[u]++, m++) {
          const a = sample(coord[0], coord[1], coord[2]);
          coord[d] += 1;
          const b = sample(coord[0], coord[1], coord[2]);
          coord[d] -= 1;
          mask[m] = a && b ? 0 : a ? a : -b;
        }
      }

      for (let j = 0; j < dims[v]; j++) {
        for (let i = 0; i < dims[u];) {
          const c = mask[j * dims[u] + i];
          if (!c) { i++; continue; }

          let w = 1; while (i + w < dims[u] && mask[j * dims[u] + i + w] === c) w++;
          let h = 1; outer: for (; j + h < dims[v]; h++) {
            for (let k = 0; k < w; k++)
              if (mask[(j + h) * dims[u] + i + k] !== c) break outer;
          }

          for (let jj = 0; jj < h; jj++)
            for (let ii = 0; ii < w; ii++)
              mask[(j + jj) * dims[u] + i + ii] = 0;

          const du = [0, 0, 0]; du[u] = w;
          const dv = [0, 0, 0]; dv[v] = h;
          const x0 = [coord[0], coord[1], coord[2]]; x0[u] = i; x0[v] = j;

          const p0 = world(add(x0, q));
          const p1 = world(add(add(x0, q), du));
          const p2 = world(add(add(add(x0, q), du), dv));
          const p3 = world(add(add(x0, q), dv));

          const normal = [0, 0, 0]; normal[d] = c > 0 ? 1 : -1;
          const colour = new THREE.Color(palette[Math.abs(c)] ?? 0xffffff).toArray();

          if (c > 0) {
            pushQuad(p0, p1, p2, p3, normal, colour);
          } else {
            pushQuad(p0, p3, p2, p1, normal, colour);
          }

          i += w;
        }
      }
    }
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geometry.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geometry.setAttribute('color', new THREE.Float32BufferAttribute(col, 3));
  geometry.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geometry.setIndex(idx);
  geometry.computeBoundingSphere();

  return geometry;

  function pushQuad(a: number[], b: number[], c: number[], d: number[], N: number[], C: number[]) {
    pos.push(...a, ...b, ...c, ...a, ...c, ...d);
    nrm.push(...repeat(N, 6));
    col.push(...repeat(C, 6));
    uv.push(0, 0, 1, 0, 1, 1, 0, 0, 1, 1, 0, 1);
    idx.push(vert, vert + 1, vert + 2, vert + 3, vert + 4, vert + 5);
    vert += 6;
  }

  function sample(x: number, y: number, z: number) {
    if (x < 0 || y < 0 || z < 0 || x >= sx || y >= sy || z >= sz) return 0;
    return voxels[x + sx * (y + sy * z)];
  }

  function add(a: number[], b: number[]) { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; }
  function world(v: number[]) { return [v[0] * scale, v[1] * scale, v[2] * scale]; }
  function repeat(arr: number[] | number, n: number) { return typeof arr === 'number' ? Array(n).fill(arr) : Array(n).fill(arr).flat(); }
}
